<form [formGroup]="pageForm" (ngSubmit)="onSubmit()" (keydown.enter)="onFormEnter($any($event))"
	class="main-content mb-4 mx-lg-4 mx-md-4 mx-sm-0 px-sm-4 px-3">

	<ms-page-title />

	@if (isEditMode() && isLoadingBudget()) {
		<div class="text-center py-5">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading...</span>
			</div>
		</div>
	} @else {

	<!-- Year -->
	<div class="row g-3 mb-4">
		<div class="col-12 col-sm-6 col-md-3">
			<ms-input-label [labelText]="'Year'" [isRequired]="true" />
			<input type="number" formControlName="year" class="form-control"
				min="2000" max="2100" step="1" inputmode="numeric" placeholder="YYYY"
				[class.is-invalid]="pageForm.get('year')?.invalid && pageForm.get('year')?.touched" />
			@if (pageForm.get('year')?.invalid && pageForm.get('year')?.touched) {
				<p class="text-danger text-start pt-1 mb-0">
					@if (pageForm.get('year')?.errors?.['required']) {
						{{ FormValidatorMessages.BudgetYearRequired }}
					} @else if (pageForm.get('year')?.errors?.['taken']) {
						{{ FormValidatorMessages.BudgetYearTaken }}
					} @else {
						{{ FormValidatorMessages.BudgetYearRange }}
					}
				</p>
			}
		</div>
	</div>

	<!-- Aggregate validation message (desktop; per-row messages render on mobile card layout) -->
	@if (hasInvalidAmountRow() || hasMissingAccountRow()) {
		<p class="text-danger fs-6 fw-medium mb-2 budget-summary-error">
			@if (hasMissingAccountRow()) {
				{{ FormValidatorMessages.BudgetAccountRequired }}
			} @else {
				{{ FormValidatorMessages.BudgetAmountMin }}
			}
		</p>
	}

	<!-- Budget line grid -->
	<div class="budget-lines">
		<div class="budget-header">
			<div class="budget-header-cell">Type</div>
			<div class="budget-header-cell budget-required">Account</div>
			<div class="budget-header-cell budget-required">Annual Budget Amount</div>
			<div class="budget-header-cell"></div>
		</div>

		<div formArrayName="lines">
			@for (line of lines.controls; track rowIds[$index]; let i = $index) {
				<div class="budget-line" [formGroupName]="i">
					@let account = getAccountForLine(i);

					<div class="budget-field budget-field-type">
						<label class="budget-mobile-label">Type</label>
						@if (account) {
							<span class="badge"
								[class.bg-success-subtle]="account.category === 'Revenue'"
								[class.text-success]="account.category === 'Revenue'"
								[class.bg-warning-subtle]="account.category === 'Expenses'"
								[class.text-warning-emphasis]="account.category === 'Expenses'">
								{{ formatAccountCategoryLabel(account.category) }}
							</span>
							@if (!account.isActive) {
								<span class="badge bg-warning-subtle text-warning-emphasis ms-1">Retired</span>
							}
						} @else {
							<span class="badge bg-secondary-subtle text-secondary">Not Selected</span>
						}
					</div>

					<div class="budget-field budget-field-account">
						<label class="budget-mobile-label">
							Account<span class="budget-required-dot"></span>
						</label>
						<ms-chart-of-account-typeahead
							formControlName="chartOfAccountsId"
							[organizationId]="userSessionService.activeOrganization()?.organizationId"
							[categories]="[ChartOfAccountCategory.Revenue, ChartOfAccountCategory.Expenses]"
							[excludeIds]="excludeIdsForRow(i)"
							(accountSelected)="onAccountSelected(i, $event)"
							[invalid]="getLineGroup(i).get('chartOfAccountsId')?.invalid && getLineGroup(i).get('chartOfAccountsId')?.touched ? true : false" />
						@if (getLineGroup(i).get('chartOfAccountsId')?.invalid && getLineGroup(i).get('chartOfAccountsId')?.touched) {
							<p class="text-danger text-start pt-1 mb-0 budget-mobile-error">{{ FormValidatorMessages.BudgetAccountRequired }}</p>
						}
					</div>

					<div class="budget-field budget-field-amount">
						<label class="budget-mobile-label">
							Annual Budget Amount<span class="budget-required-dot"></span>
						</label>
						<ms-amount-input formControlName="amount" />
						@if (getLineGroup(i).get('amount')?.invalid && getLineGroup(i).get('amount')?.touched) {
							<p class="text-danger text-start pt-1 mb-0 budget-mobile-error">
								@if (getLineGroup(i).get('amount')?.errors?.['required']) {
									{{ FormValidatorMessages.BudgetAmountRequired }}
								} @else if (getLineGroup(i).get('amount')?.errors?.['min']) {
									{{ FormValidatorMessages.BudgetAmountMin }}
								}
							</p>
						}
					</div>

					<div class="budget-field budget-field-action">
						<button type="button"
							class="btn btn-link p-0 budget-remove-btn text-danger"
							(click)="removeRow(i)"
							title="Remove row">
							<fa-duotone-icon icon="trash" secondaryOpacity="0.9" />
						</button>
					</div>
				</div>
			} @empty {
				<div class="budget-empty text-muted text-center py-4">
					Click on the “+Add Row” button below to begin adding accounts.
				</div>
			}
		</div>
	</div>

	<!-- Add Row Button -->
	<div class="mt-3 mb-4">
		<button type="button" class="btn btn-ms-secondary" (click)="addRow()">
			<fa-duotone-icon icon="plus" secondaryOpacity="0.9" class="me-1" />
			Add Row
		</button>
	</div>

	<!-- Totals -->
	@if (lines.length > 0) {
		<div class="row mb-4 mt-3">
			<div class="col-12">
				<div class="d-flex flex-wrap gap-4 align-items-center justify-content-end p-3 rounded">
					<div>
						<span class="text-muted">Total Revenue:</span>
						<span class="fw-medium text-success ms-1">{{ formatCurrency(totalRevenue()) }}</span>
					</div>
					<div>
						<span class="text-muted">Total Expenses:</span>
						<span class="fw-medium text-danger ms-1">{{ formatCurrency(totalExpenses()) }}</span>
					</div>
				</div>
			</div>
		</div>
	}

	<!-- Actions -->
	<div class="d-flex flex-column flex-sm-row justify-content-end gap-2 mt-3 align-items-stretch align-items-sm-center">
		<button type="button" class="btn btn-ms-secondary order-2 order-sm-1" (click)="onCancel()">
			Cancel
		</button>
		<ng-container *ifHasPermission="{ requiredPermissions: requiredSavePermissions(), userPermissionOnly: false, dataEntityType: DataEntityTypes.User }">
			<button type="submit" class="btn btn-primary order-1 order-sm-2"
				[disabled]="isSubmitting || lines.length === 0 || isLoadingBudget()">
				@if (isSubmitting) {
					<div class="spinner-border text-light spinner-border-sm" role="status">
						<span class="visually-hidden">Loading...</span>
					</div>
				} @else {
					Save
				}
			</button>
		</ng-container>
	</div>

	}
</form>

<ms-confirmation-modal #budgetConfirmationModalRef modalId="budgetConfirmationModal"
	(confirmed)="confirmationConfirmFunction($event)"
	(cancelled)="confirmationCancelFunction()" />

<ms-info-modal #budgetInfoModalRef modalId="budgetInfoModal" (onConfirm)="infoModalConfirmed()" />
